import { createContext, useCallback, useContext, useMemo } from 'react';
import type { ReactNode } from 'react';
import { useLocalStorage } from '@/hooks/use-local-storage';
import { getModel, isModelId, type ModelConfig, type ModelId, type ReasoningEffort } from './types';

const MODEL_KEY = 'jenny-model';
const REASONING_KEY = 'jenny-reasoning-effort';
const DEFAULT_MODEL: ModelId = 'gpt-6.1-sol';
const DEFAULT_REASONING: ReasoningEffort = 'low';

export interface ModelContextValue {
  modelId: ModelId;
  reasoningEffort: ReasoningEffort;
  setModelId: (id: string) => void;
  setReasoningEffort: (effort: string) => void;
}

function resolveEffort(model: ModelConfig, effort: unknown): ReasoningEffort {
  const allowed = model.reasoningEfforts.find((e) => e === effort);
  return allowed ?? model.defaultReasoning ?? DEFAULT_REASONING;
}

const ModelContext = createContext<ModelContextValue | null>(null);

export function ModelProvider({ children }: { children: ReactNode }) {
  const [storedModelId, setModelIdRaw] = useLocalStorage<unknown>(MODEL_KEY, DEFAULT_MODEL);
  const [storedEffort, setReasoningEffortRaw] = useLocalStorage<unknown>(
    REASONING_KEY,
    DEFAULT_REASONING,
  );

  const modelId = isModelId(storedModelId) ? storedModelId : DEFAULT_MODEL;
  const reasoningEffort = resolveEffort(getModel(modelId), storedEffort);

  const setModelId = useCallback(
    (value: string) => {
      if (!isModelId(value)) return;
      setModelIdRaw(value);
      setReasoningEffortRaw(resolveEffort(getModel(value), reasoningEffort));
    },
    [setModelIdRaw, setReasoningEffortRaw, reasoningEffort],
  );

  const setReasoningEffort = useCallback(
    (value: string) => {
      setReasoningEffortRaw(value);
    },
    [setReasoningEffortRaw],
  );

  const value = useMemo<ModelContextValue>(
    () => ({ modelId, reasoningEffort, setModelId, setReasoningEffort }),
    [modelId, reasoningEffort, setModelId, setReasoningEffort],
  );

  return <ModelContext.Provider value={value}>{children}</ModelContext.Provider>;
}

export function useModel(): ModelContextValue {
  const ctx = useContext(ModelContext);
  if (!ctx) throw new Error('useModel must be used within ModelProvider');
  return ctx;
}
